import { AsyncPipe } from '@angular/common';
import {
  Component,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  ViewEncapsulation,
} from '@angular/core';

import { getShortcutLabel, ShortcutsManager } from '@ideals/services/shortcuts-manager';

import { ButtonComponent } from '../button';
import { IconButtonComponent } from '../icon-button';
import { ProgressCircleComponent } from '../progress-circle';

import { ToastMessage, ToastMessageButton } from './toast.model';

@Component({
  encapsulation: ViewEncapsulation.None,
  host: {
    'animate.leave': 'ideals-toast--leave',
    class: 'ideals-toast',
    '[class]': 'message().severity',
    '(mouseenter)': 'stopDelayedClosing()',
    '(mouseleave)': 'startDelayedClosing()',
    '[attr.data-ideals]': '"ideals-toast-" + message().id',
  },
  imports: [
    AsyncPipe,
    ButtonComponent,
    IconButtonComponent,
    ProgressCircleComponent,
  ],
  selector: 'ideals-toast',
  styleUrl: './toast.component.scss',
  templateUrl: './toast.component.html',
})
export class ToastComponent implements OnInit {
  readonly message = input.required<ToastMessage>();

  readonly closed = output();

  readonly #element = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  readonly #shortcutsManager = new ShortcutsManager();

  #closeTimeout?: ReturnType<typeof setTimeout>;

  protected readonly getShortcutLabel = getShortcutLabel;

  ngOnInit(): void {
    this.#initShortcuts();
    this.startDelayedClosing();
  }

  close(): void {
    this.#element.style.setProperty('--toast-height', `${this.#element.offsetHeight}px`);
    this.stopDelayedClosing();
    this.#shortcutsManager.destroy();
    this.closed.emit();
  }

  protected handleButtonClick(button: ToastMessageButton): void {
    button.command();
    this.close();
  }

  protected startDelayedClosing(): void {
    const message = this.message();

    if (message.life && !message.sticky) {
      this.#closeTimeout = setTimeout(() => {
        this.close();
      }, message.life);
    }
  }

  protected stopDelayedClosing(): void {
    if (this.#closeTimeout) {
      clearTimeout(this.#closeTimeout);
    }
  }

  #initShortcuts(): void {
    const buttons = this.message().buttons;

    this.#shortcutsManager.scope(`toast-${this.message().id}`);

    if (buttons) {
      buttons.forEach((button) => {
        if (button.shortcut) {
          this.#shortcutsManager.add(button.shortcut, () => this.handleButtonClick(button));
        }
      });
    }

    this.#shortcutsManager.merge();
  }
}
